/**
 * Date arithmetic and formatting for the date picker.
 *
 * Deliberately dependency-free and built on the platform `Date`. Every value is
 * a *local* midnight, so day comparisons never straddle a timezone boundary.
 */

/** Figma `Type=Dual dates` selects a range; either end may still be unset. */
export type DateRange = { start: Date | null; end: Date | null };

/**
 * Figma's grid reads `Mo Tu We Th Fr Sa Su`, so the week starts on Monday. The
 * design defines no other order, so this is fixed rather than configurable.
 */
export const WEEKDAYS = [
  { short: 'Mo', long: 'Monday' },
  { short: 'Tu', long: 'Tuesday' },
  { short: 'We', long: 'Wednesday' },
  { short: 'Th', long: 'Thursday' },
  { short: 'Fr', long: 'Friday' },
  { short: 'Sa', long: 'Saturday' },
  { short: 'Su', long: 'Sunday' },
] as const;

/**
 * Figma's `Dates` frame is 312x312 with 16px padding — 280x280 of cells, which
 * is exactly 7 columns and 7 rows of 40px. One of those rows is the weekday
 * header, so a month always renders 6 week rows and pads with adjacent-month
 * days, as both January and February do in the design.
 */
export const WEEKS_IN_GRID = 6;
export const DAYS_IN_WEEK = 7;

export function startOfDay(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

export function startOfMonth(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), 1);
}

export function addDays(date: Date, days: number): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate() + days);
}

export function addMonths(date: Date, months: number): Date {
  return new Date(date.getFullYear(), date.getMonth() + months, 1);
}

export function isSameDay(a: Date | null | undefined, b: Date | null | undefined): boolean {
  if (!a || !b) return false;
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

export function isSameMonth(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth();
}

/** Inclusive on both ends. */
export function isWithin(date: Date, start: Date | null, end: Date | null): boolean {
  if (!start || !end) return false;
  const t = startOfDay(date).getTime();
  return t >= startOfDay(start).getTime() && t <= startOfDay(end).getTime();
}

/** Puts a range the user dragged backwards the right way round. */
export function orderRange(range: DateRange): DateRange {
  const { start, end } = range;
  if (start && end && startOfDay(end).getTime() < startOfDay(start).getTime()) {
    return { start: end, end: start };
  }
  return range;
}

/**
 * The 42 days of a month grid, Monday-first, starting on the Monday on or
 * before the 1st. January 2025 therefore opens on Dec 30 and closes on Feb 9 —
 * exactly the cells Figma's `Left picker` shows.
 */
export function buildMonthGrid(month: Date): Date[] {
  const first = startOfMonth(month);
  // getDay() is Sunday-based; shift so Monday is 0.
  const leading = (first.getDay() + 6) % DAYS_IN_WEEK;
  const origin = addDays(first, -leading);
  return Array.from({ length: WEEKS_IN_GRID * DAYS_IN_WEEK }, (_, i) => addDays(origin, i));
}

/** Splits the flat grid into the six week rows the table renders. */
export function toWeeks(days: Date[]): Date[][] {
  const weeks: Date[][] = [];
  for (let i = 0; i < days.length; i += DAYS_IN_WEEK) weeks.push(days.slice(i, i + DAYS_IN_WEEK));
  return weeks;
}

/**
 * `en-US` rather than the runtime locale so the rendered strings are stable —
 * the design is English, and a locale-dependent title would make the parity
 * measurements depend on the machine running them. Override with `locale`.
 */
export const DEFAULT_LOCALE = 'en-US';

/** Figma: "January 2025". */
export function formatMonthTitle(month: Date, locale: string = DEFAULT_LOCALE): string {
  return new Intl.DateTimeFormat(locale, { month: 'long', year: 'numeric' }).format(month);
}

/** Figma: "Jan 10, 2025". */
export function formatDate(date: Date, locale: string = DEFAULT_LOCALE): string {
  return new Intl.DateTimeFormat(locale, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(date);
}

/** The accessible name of a day cell — "Friday, January 10, 2025". */
export function formatDayLabel(date: Date, locale: string = DEFAULT_LOCALE): string {
  return new Intl.DateTimeFormat(locale, {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  }).format(date);
}

/** Figma: "Jan 10, 2025 – Jan 16, 2025" — an en dash with spaces. */
export function formatRange(range: DateRange, locale: string = DEFAULT_LOCALE): string | null {
  const { start, end } = range;
  if (!start && !end) return null;
  if (start && end) return `${formatDate(start, locale)} – ${formatDate(end, locale)}`;
  return formatDate((start ?? end) as Date, locale);
}
